import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { useTrackingEvents } from '@/hooks/useTrackingEvents';
import { ArrowLeft, MapPin, Calendar, Package, Upload, CheckCircle } from 'lucide-react';
import { Loader } from '@/components/ui/loader';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useState } from 'react';

export default function OrderScan() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { createTrackingEvent, isCreating } = useTrackingEvents(orderId);
  const [podFile, setPodFile] = useState<File | null>(null);

  const { data: order, isLoading } = useQuery({
    queryKey: ['order-scan', orderId],
    queryFn: async () => {
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select('*, transport_jobs(*)')
        .eq('id', orderId)
        .single();

      if (orderError) throw orderError;
      return orderData;
    },
  });

  const getCurrentLocation = (): Promise<{ latitude: number; longitude: number }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => reject(error)
      );
    });
  };

  const uploadPOD = async (file: File): Promise<string> => {
    const fileName = `pod/${orderId}/${Date.now()}.jpg`;
    const { error: uploadError } = await supabase.storage
      .from('order-images')
      .upload(fileName, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage.from('order-images').getPublicUrl(fileName);
    return data.publicUrl;
  };

  const handleStatusChange = async (eventType: 'pickup_confirmed' | 'delivered') => {
    try {
      if (eventType === 'delivered' && !podFile) {
        toast({
          title: 'Photo requise',
          description: 'Veuillez prendre une photo du bon de livraison signé',
          variant: 'destructive',
        });
        return;
      }

      let location;
      try {
        location = await getCurrentLocation();
      } catch (error) {
        console.warn('Could not get location:', error);
        location = undefined;
      }

      let podUrl;
      if (podFile) {
        podUrl = await uploadPOD(podFile);
      }

      await createTrackingEvent({
        order_id: orderId!,
        event_type: eventType,
        latitude: location?.latitude,
        longitude: location?.longitude,
        photo_url: podUrl,
      });

      toast({
        title: 'Statut mis à jour',
        description: eventType === 'pickup_confirmed' 
          ? 'Enlèvement confirmé avec succès'
          : 'Livraison confirmée avec succès',
      });

      navigate('/driver/missions');
    } catch (error: any) {
      console.error('Error updating status:', error);
      const rawMessage =
        error?.message ||
        error?.error_description ||
        error?.hint ||
        (typeof error === 'string' ? error : JSON.stringify(error));
      toast({
        title: 'Impossible de confirmer',
        description: rawMessage || 'Une erreur inconnue est survenue',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader size="lg" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] p-6">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-[#5A6B7A]">Commande introuvable</p>
              <Button
                onClick={() => navigate('/driver/missions')}
                variant="outline"
                className="mt-4"
              >
                <ArrowLeft className="mr-2" size={18} />
                Retour aux missions
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const currentStatus = order.status;
  const canConfirmPickup = currentStatus === 'assigned';
  const canConfirmDelivery = currentStatus === 'in_transit';

  return (
    <div className="min-h-screen bg-[#F8F9FA] p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <Button
          onClick={() => navigate('/driver/missions')}
          variant="outline"
        >
          <ArrowLeft className="mr-2" size={18} />
          Retour aux missions
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-semibold text-[#37699E] font-['Lexend_Deca']">
              Commande {order.order_number}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Package className="text-[#0880AE] mt-1" size={20} />
                <div>
                  <p className="font-medium text-[#2D3748]">{order.cargo_title || 'Chargement'}</p>
                  <p className="text-sm text-[#5A6B7A]">{order.cargo_description}</p>
                  <p className="text-sm text-[#5A6B7A] mt-1">{order.weight_kg} kg</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="text-[#14A38B] mt-1" size={20} />
                <div>
                  <p className="text-sm font-medium text-[#2D3748]">Enlèvement</p>
                  <p className="text-sm text-[#5A6B7A]">
                    {order.pickup_address || `${order.pickup_city}, ${order.pickup_country}`}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="text-[#FF7171] mt-1" size={20} />
                <div>
                  <p className="text-sm font-medium text-[#2D3748]">Livraison</p>
                  <p className="text-sm text-[#5A6B7A]">
                    {order.delivery_address || `${order.delivery_city}, ${order.delivery_country}`}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Calendar className="text-[#0880AE] mt-1" size={20} />
                <div>
                  <p className="text-sm font-medium text-[#2D3748]">Dates</p>
                  <p className="text-sm text-[#5A6B7A]">
                    Enlèvement: {format(new Date(order.pickup_date), 'PPP', { locale: fr })}
                  </p>
                  <p className="text-sm text-[#5A6B7A]">
                    Livraison: {format(new Date(order.delivery_date), 'PPP', { locale: fr })}
                  </p>
                </div>
              </div>
            </div>

            {canConfirmDelivery && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#2D3748]">
                  Photo du bon de livraison signé (POD) *
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => setPodFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="pod-upload"
                  />
                  <label
                    htmlFor="pod-upload"
                    className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-[#DBE2EA] rounded-lg cursor-pointer hover:border-[#0880AE] transition-colors"
                  >
                    <Upload size={18} className="text-[#5A6B7A]" />
                    <span className="text-sm text-[#5A6B7A]">
                      {podFile ? podFile.name : 'Prendre une photo'}
                    </span>
                  </label>
                </div>
              </div>
            )}

            <div className="space-y-3 pt-4">
              {canConfirmPickup && (
                <Button
                  onClick={() => handleStatusChange('pickup_confirmed')}
                  disabled={isCreating}
                  className="w-full bg-[#14A38B] hover:bg-[#10836F] text-white"
                >
                  <CheckCircle className="mr-2" size={18} />
                  Confirmer l'enlèvement
                </Button>
              )}

              {canConfirmDelivery && (
                <Button
                  onClick={() => handleStatusChange('delivered')}
                  disabled={isCreating || !podFile}
                  className="w-full bg-[#14A38B] hover:bg-[#10836F] text-white"
                >
                  <CheckCircle className="mr-2" size={18} />
                  Confirmer la livraison
                </Button>
              )}

              {!canConfirmPickup && !canConfirmDelivery && (
                <div className="p-4 bg-[#0880AE]/10 border border-[#0880AE]/20 rounded-lg text-center space-y-1">
                  <p className="text-sm text-[#0880AE] font-medium">
                    Aucune action disponible pour le moment
                  </p>
                  <p className="text-xs text-[#5A6B7A]">
                    Statut actuel : <span className="font-semibold">{currentStatus}</span>.{' '}
                    {currentStatus === 'delivered'
                      ? 'La livraison a déjà été confirmée.'
                      : currentStatus === 'booked'
                      ? "Le transporteur doit d'abord vous assigner à cette mission."
                      : currentStatus === 'posted' || currentStatus === 'draft'
                      ? "Cette commande n'a pas encore été réservée."
                      : "L'enlèvement doit être confirmé avant de pouvoir livrer."}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
